import { useState } from 'react';

import Modal from 'components/partials/modal/modal';
import Loader from 'components/partials/loader/loader';
import FavoriteReportTile from '../farovite-report-tile/farovite-report-tile';

import type { JsonNode } from 'api/generated/schemas';

import { useCurrentUser } from 'state/current-user/current-user.thunk';
import {
  useFavoriteReports,
  useUpdateFavoriteReport,
} from 'hooks/api/use-favorite-reports';

import useProductTypesDictionary from '../../use-product-types-dictionary';

import type { FavoriteReportData } from '../farovite-report-tile/farovite-report-tile';
import type { FavoriteReportFilter } from '../filter-item/filter-item';

interface FavoriteMetadata extends JsonNode {
  filters?: FavoriteReportFilter[];
}

interface UpdateModalContentProps {
  onClose: () => void;
  bookmarkState?: string | null;
  metadata?: FavoriteMetadata | null;
  reportId: string | number;
}

const UpdateModalContent = ({
  onClose,
  bookmarkState,
  metadata = null,
  reportId,
}: UpdateModalContentProps): React.JSX.Element => {
  const currentUser = useCurrentUser();
  const [selectedFavoriteReport, setSelectedFavoriteReport] =
    useState<FavoriteReportData | null>(null);
  const { dictionary: productTypesDictionary } = useProductTypesDictionary();
  const { data: favoriteReports, loading } = useFavoriteReports();
  const updateMutation = useUpdateFavoriteReport();

  const favoriteReportsList = favoriteReports as
    | FavoriteReportData[]
    | null
    | undefined;

  const handleClose = (): void => {
    setSelectedFavoriteReport(null);
    onClose();
  };

  const handleSubmitUpdate = async (): Promise<void> => {
    await updateMutation.mutateAsync({
      oktaUserId: currentUser.data!.oktaUserId,
      userId: Number(currentUser.data!.id),
      bookmarkId: Number(selectedFavoriteReport!.id),
      data: {
        reportId: Number(reportId),
        name: selectedFavoriteReport!.name,
        bookmarkState: bookmarkState ?? undefined,
        metadata: metadata ?? undefined,
      },
    });

    handleClose();
  };

  return (
    <Modal
      headerText="Select favorite item to update"
      modalClassName="!max-w-[950px] !w-[950px] min-h-[360px]"
      onConfirmButtonLabel="Submit"
      onConfirmCallback={handleSubmitUpdate}
      onCloseCallback={handleClose}
      disabled={!selectedFavoriteReport}
      loading={updateMutation.isPending}
    >
      {loading ? (
        <Loader loading customClass="!bg-transparent" />
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6 mt-4 max-h-[420px] overflow-y-auto">
          {favoriteReportsList?.map((favoriteReport) => (
            <FavoriteReportTile
              key={favoriteReport.id}
              favoriteReport={favoriteReport}
              productTypesDictionary={productTypesDictionary}
              showActions={false}
              isSelected={selectedFavoriteReport?.id === favoriteReport.id}
              onClick={() => setSelectedFavoriteReport(favoriteReport)}
            />
          ))}
        </div>
      )}
    </Modal>
  );
};

export default UpdateModalContent;
